import { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft,
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertCircle,
  ExternalLink,
  Clock,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  type ApiKeyInfo,
} from '@/hooks/use-api-keys';
import { toast } from 'sonner';

const ALL_SCOPES = [
  { value: 'strategies:read', label: 'Read strategies' },
  { value: 'strategies:write', label: 'Create & edit strategies' },
  { value: 'backtests:run', label: 'Run backtests' },
  { value: 'bots:read', label: 'Read bots' },
  { value: 'bots:write', label: 'Create & manage bots' },
  { value: 'accounts:read', label: 'Read accounts & portfolio' },
  { value: 'analysis:run', label: 'Upload & analyze trades' },
];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function ApiKeysPage() {
  const { data: keys, isLoading } = useApiKeys();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(
    ALL_SCOPES.map((s) => s.value),
  );
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyInfo | null>(null);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const result = await createMutation.mutateAsync({
        name: newKeyName.trim(),
        scopes: selectedScopes,
      });
      setRawKey(result.key);
      setNewKeyName('');
      setSelectedScopes(ALL_SCOPES.map((s) => s.value));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleCopy = async () => {
    if (!rawKey) return;
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    toast.success('API key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast.success('API key revoked');
      setRevokeTarget(null);
    } catch {
      toast.error('Failed to revoke API key');
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Header */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-[11px] text-ink-3 hover:text-ink transition-colors"
      >
        <ArrowLeft size={12} /> Dashboard
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold">API Keys</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            Manage API keys for programmatic access and MCP integrations
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={14} /> New Key
        </Button>
      </div>

      {/* Key list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : keys && keys.length > 0 ? (
        <div className="space-y-2">
          {keys.map((k) => (
            <div
              key={k.id}
              className="glass-sm rounded-lg p-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Key size={13} className="text-orange flex-shrink-0" />
                  <p className="text-sm font-bold truncate">{k.name}</p>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <code className="text-[10px] font-mono text-ink-3 bg-surface px-1.5 py-0.5 rounded">
                    {k.key_prefix}...
                  </code>
                  <span className="text-[10px] text-ink-4 flex items-center gap-1">
                    <Shield size={9} />
                    {k.scopes.length} scope{k.scopes.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-[10px] text-ink-4 flex items-center gap-1">
                    <Clock size={9} />
                    {k.last_used_at ? timeAgo(k.last_used_at) : 'Never used'}
                  </span>
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setRevokeTarget(k)}
              >
                <Trash2 size={12} /> Revoke
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-sm rounded-lg p-8 text-center">
          <Key size={32} className="mx-auto text-ink-4 mb-3" />
          <p className="text-sm font-semibold text-ink-3">No API keys yet</p>
          <p className="text-[11px] text-ink-4 mt-1">
            Create an API key to connect Claude Desktop, custom scripts, or
            third-party apps.
          </p>
        </div>
      )}

      {/* MCP Setup instructions */}
      <div className="glass-sm rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <ExternalLink size={12} className="text-orange" />
          <p className="text-[11px] font-bold text-ink-3 uppercase">
            Connect with Claude Desktop (MCP)
          </p>
        </div>
        <p className="text-[11px] text-ink-3">
          Add this to your Claude Desktop config to connect ForgeAlpha as an MCP
          server:
        </p>
        <pre className="text-[10px] font-mono bg-surface rounded-lg p-3 overflow-x-auto text-ink-2">
{`{
  "mcpServers": {
    "forgealpha": {
      "command": "npx",
      "args": ["-y", "forgealpha-mcp"],
      "env": {
        "FORGEALPHA_API_KEY": "your-api-key-here",
        "FORGEALPHA_API_URL": "${import.meta.env.VITE_MCP_API_URL || 'https://beta.api.forgealpha.cloud'}"
      }
    }
  }
}`}
        </pre>
      </div>

      {/* Settings nav */}
      <div className="flex gap-2">
        <Link to="/settings/connected-apps" className="flex-1">
          <div className="glass-sm rounded-lg p-3 hover:bg-surface transition-colors text-center">
            <p className="text-[11px] font-bold text-ink-2">Connected Apps</p>
            <p className="text-[10px] text-ink-4 mt-0.5">Manage third-party app access</p>
          </div>
        </Link>
        <Link to="/settings/developer" className="flex-1">
          <div className="glass-sm rounded-lg p-3 hover:bg-surface transition-colors text-center">
            <p className="text-[11px] font-bold text-ink-2">Developer</p>
            <p className="text-[10px] text-ink-4 mt-0.5">Register OAuth apps</p>
          </div>
        </Link>
      </div>

      {/* Create modal */}
      <Modal
        open={showCreate && !rawKey}
        onClose={() => {
          setShowCreate(false);
          setNewKeyName('');
          setSelectedScopes(ALL_SCOPES.map((s) => s.value));
        }}
        title="Create API Key"
      >
        <div className="space-y-4">
          <Input
            label="Key Name"
            placeholder="e.g. Claude Desktop, My Script"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            autoFocus
          />

          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-2">
              Permissions
            </label>
            <div className="space-y-1.5">
              {ALL_SCOPES.map((s) => (
                <label
                  key={s.value}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-surface transition-colors cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(s.value)}
                    onChange={() => toggleScope(s.value)}
                    className="accent-orange w-3.5 h-3.5"
                  />
                  <span className="text-xs">{s.label}</span>
                  <code className="text-[9px] font-mono text-ink-4 ml-auto">
                    {s.value}
                  </code>
                </label>
              ))}
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleCreate}
            disabled={!newKeyName.trim() || selectedScopes.length === 0 || createMutation.isPending}
          >
            {createMutation.isPending ? <Spinner size="sm" /> : <Key size={14} />}
            Create API Key
          </Button>
        </div>
      </Modal>

      {/* Show raw key modal */}
      <Modal
        open={!!rawKey}
        onClose={() => {
          setRawKey(null);
          setShowCreate(false);
        }}
        title="API Key Created"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
            <AlertCircle size={14} className="text-amber flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber/90">
              Copy this key now — it won't be shown again.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] font-mono bg-surface border border-glass-border rounded-lg px-3 py-2.5 break-all select-all">
              {rawKey}
            </code>
            <button
              onClick={handleCopy}
              className="flex-shrink-0 w-9 h-9 rounded-lg bg-surface border border-glass-border flex items-center justify-center text-ink-3 hover:text-orange transition-colors cursor-pointer"
            >
              {copied ? <Check size={14} className="text-green" /> : <Copy size={14} />}
            </button>
          </div>

          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setRawKey(null);
              setShowCreate(false);
            }}
          >
            Done
          </Button>
        </div>
      </Modal>

      {/* Revoke confirmation modal */}
      <Modal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API Key"
      >
        <div className="space-y-4">
          <p className="text-xs text-ink-2">
            Are you sure you want to revoke{' '}
            <span className="font-bold">{revokeTarget?.name}</span>? Any
            applications using this key will immediately lose access.
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => setRevokeTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={12} />}
              Revoke Key
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
